import './grid.css';

import {
    ColumnDef,
    ColumnFiltersState,
    ColumnPinningState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    Row,
    RowSelectionState,
    SortingState,
    Table as TableType,
    useReactTable,
} from '@tanstack/react-table';
import { inRange } from 'radash';
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { Loader } from '../Loader';
import { PaginationPage } from '../PaginationPage/PaginationPage';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../Table';
import { measureColumnAutoFitWidth } from './autoFitColumnWidth';
import GridBurgerMenu from './GridBurgerMenu';
import { isInteractiveCellTarget } from './gridInteraction';
import { GridRow } from './GridRow';
import { GridSortingCaret } from './GridSortingCaret';
import {
    isNearScrollBottom,
    isPastInfiniteScrollThreshold,
    isUnfilledScrollContainer,
} from './infiniteScroll';
import { useGridPreferences } from './useGridPreferences';
import { usePersistedGridState } from './usePersistedGridState';

export interface GridRef {
    focus: () => void;
    scrollToTop: () => void;
    resetColumnsSort: (e: any) => void;
    goToLastPage: () => void;
    setRowSelection: (state: RowSelectionState) => void;
}

interface GridProps<TData, TValue> {
    className?: string;
    style?: any;
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    enableSorting?: boolean;
    // LAX-10104: keep caller/backend row order; header clicks still update sort state.
    manualSorting?: boolean;
    burgerMenu?: boolean;
    isLoading?: boolean;
    isLoadingNextPage?: boolean;
    initialSortingState?: SortingState;
    //FIXME in next phase for item editor usage
    insideItemEditor?: boolean;
    onInfiniteScroll?: () => void;
    onSelectedRowsChange?: (value: TData[]) => void;
    onSelectedRow?: (row: TData) => void;
    storedStateKey?: string;
    // LAX-10039: sub-scope for the persisted grid state, used by the browse
    // items list to store column order/widths/sort per category so switching
    // between categories no longer clobbers each other's state. Grids that
    // don't need per-context state (catalog, editor history, ...) omit it.
    storedStateSubKey?: string;
    toolbar?: (table: TableType<TData>) => JSX.Element;
    id?: string;
    onSortingChange?: (storedState: any) => void;
    thClassName?: string;
    isViewer?:boolean
    isWrapContent?: boolean;
    enableRowSelection?: boolean;
    shouldShowPaginationFlag?: boolean;
    /**
     * Forwarded to `PaginationPage` when pagination is shown.
     * Defaults to `full` so existing callers (e.g. MultiReferenceGrid) keep
     * first/last buttons and the editable page InputNumber.
     */
    paginationNavigation?: 'full' | 'compact';
    maxHeight?: string;
    getRowId?: (row: TData) => string;
    // When true, the scroll container only scrolls vertically. Any content
    // that would overflow horizontally is clipped instead of exposing a
    // horizontal scrollbar. Intended for pages/dialogs that must not
    // introduce a horizontal scroll (e.g. dashboards with wide tables where
    // columns are expected to fit the available width).
    disableHorizontalScroll?: boolean;
    /**
     * Optional wrapper applied around each row's cells. Called with the
     * TanStack row instance and the default cell nodes; the returned node
     * replaces the cell list. Consumers use this to mount a per-row
     * `<FormProvider>` (or other row-scoped context) only where it is
     * needed, avoiding the cost of wrapping every row in the table.
     */
    rowWrapper?: (row: Row<TData>, children: ReactNode) => ReactNode;
    /**
     * When true, double-clicking a column's right resize border auto-fits
     * that column to the longest visible header / cell text. Opt-in so
     * editor and other shared Grid consumers stay unchanged. Used by the
     * Items and Catalog hit lists to match the legacy ui-grid.
     */
    autoFitColumnOnResizeHandleDoubleClick?: boolean;
    /**
     * When true, long header labels ellipsize inside the column width.
     * Opt-in so editor and other shared Grid consumers stay unchanged.
     * Used by the Items and Catalog hit lists.
     */
    truncateHeaders?: boolean;
}

const EMPTY_SORTING: SortingState = [];

const Grid = forwardRef<GridRef, GridProps<any, any>>(({
    className,
    columns,
    data,
    enableSorting = false,
    manualSorting = false,
    isLoading = false,
    isLoadingNextPage = false,
    initialSortingState,
    onInfiniteScroll,
    onSelectedRowsChange,
    onSelectedRow,
    storedStateKey,
    storedStateSubKey,
    toolbar,
    burgerMenu = false,
    insideItemEditor = false,
    id,
    onSortingChange,
    thClassName,
    isWrapContent,
    enableRowSelection = true,
    shouldShowPaginationFlag = false,
    paginationNavigation = 'full',
    maxHeight ='',
    getRowId,
    disableHorizontalScroll = false,
    rowWrapper,
    autoFitColumnOnResizeHandleDoubleClick = false,
    truncateHeaders = false,
}: GridProps<any, any>, ref: any) => {

    const ACTIONS_COL = 'actions';

    const { storedState, setStoredState } = usePersistedGridState(storedStateKey, insideItemEditor, storedStateSubKey);
    const {
        columnSizing,
        setColumnSizing,
        columnVisibility,
        setColumnVisibility,
        sortingState,
        setSortingState,
        updateGridPreferences,
        deleteGridPreferences,
        columnOrder,
        setColumnOrder,
        columnPinning,
        setColumnPinning,
    } = useGridPreferences(
        columns,
        storedState,
        setStoredState,
        storedStateKey,
        storedStateSubKey,
        initialSortingState ?? EMPTY_SORTING,
    );

    const { t } = useTranslation();
    const infiniteScrollRef = useRef<HTMLDivElement>(null);
    const gridContainerRef = useRef<HTMLDivElement>(null);

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const nonPaginatedPagination = useMemo<PaginationState>(() => {
        return {
            pageIndex: 0,
            pageSize: Math.max(data.length, 1),
        };
    }, [data.length]);
    const effectivePagination = shouldShowPaginationFlag ? pagination : nonPaginatedPagination;
    const [isResizing, setIsResizing] = useState(false);
    const [reArrangedColumns, setReArrangedColumns] = useState(storedState?.activeColumns);
    const [isBurgerMenuOpen, setIsBurgerMenuOpen] = useState(false);
    const [wrapContent, setWrapContent] = useState(true);

    const columnSizingRef = useRef(columnSizing);
    const columnVisibilityRef = useRef(columnVisibility);
    const reArrangedColumnsRef = useRef(reArrangedColumns);
    const fetchingRef = useRef(false);
    const isLoadingNextPageRef = useRef(isLoadingNextPage);
    isLoadingNextPageRef.current = isLoadingNextPage;
    const onInfiniteScrollRef = useRef(onInfiniteScroll);
    onInfiniteScrollRef.current = onInfiniteScroll;
    const wasLoadingNextPageRef = useRef(false);
    const scrollHeightAtFetchRef = useRef(0);
    const nextPageRequestIdRef = useRef(0);
    const columnStructureSignatureRef = useRef<string>('');
    // Tracks whether the most recent sortingState change was triggered by a user
    // interaction (header click) rather than by hydration from the server. Only
    // user-initiated changes should be PUT back to the server.
    const isUserSortChangeRef = useRef(false);
    // Same guard for user-driven column pin toggles: only PUT back when the
    // burger menu triggered the change, never when hydrating from the server.
    const isUserPinChangeRef = useRef(false);
    const handleTableSortingChange = (updaterOrValue: any) => {
        isUserSortChangeRef.current = true;
        setSortingState(updaterOrValue);
    };

    // Default column pinning derived from column definitions
    // (`meta.leftPinned` / `meta.pinned`) so hard-coded system columns like
    // `select` (left) and `actions` (right) stay pinned even when the user
    // has not saved any preferences yet (LAX-10037). Merged with the
    // hydrated / user-picked state from `useGridPreferences` so users can
    // additionally pin any data column via the burger menu.
    const defaultColumnPinning = useMemo<ColumnPinningState>(() => {
        const left: string[] = [];
        const right: string[] = [];
        columns.forEach((col: any) => {
            const id = String(col.id ?? col.accessorKey ?? '');
            if (!id) return;
            if (col.meta?.leftPinned) left.push(id);
            else if (col.meta?.pinned) right.push(id);
        });
        return { left, right };
    }, [columns]);

    const effectiveColumnPinning = useMemo<ColumnPinningState>(() => {
        const stateLeft = columnPinning.left ?? [];
        const stateRight = columnPinning.right ?? [];
        const mergedLeft = [
            ...(defaultColumnPinning.left ?? []).filter((id) => !stateLeft.includes(id) && !stateRight.includes(id)),
            ...stateLeft,
        ];
        const mergedRight = [
            ...stateRight,
            ...(defaultColumnPinning.right ?? []).filter((id) => !stateLeft.includes(id) && !stateRight.includes(id)),
        ];
        return { left: mergedLeft, right: mergedRight };
    }, [defaultColumnPinning, columnPinning]);

    const handleColumnPinningChange = (updaterOrValue: any) => {
        isUserPinChangeRef.current = true;
        setColumnPinning((prev) => {
            const next = typeof updaterOrValue === 'function' ? updaterOrValue(effectiveColumnPinning) : updaterOrValue;
            // Persist by stripping the hard-coded system defaults; they get
            // re-added by `effectiveColumnPinning` on the next render, so the
            // stored value only carries user-driven pins.
            const defaultLeft = defaultColumnPinning.left ?? [];
            const defaultRight = defaultColumnPinning.right ?? [];
            const userLeft = (next.left ?? []).filter((id: string) => !defaultLeft.includes(id));
            const userRight = (next.right ?? []).filter((id: string) => !defaultRight.includes(id));
            void prev;
            return { left: userLeft, right: userRight };
        });
    };

    const table = useReactTable({
        data,
        columns,
        columnResizeMode: 'onChange',
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
        getPaginationRowModel: shouldShowPaginationFlag ? getPaginationRowModel() : undefined,
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: setRowSelection,
        onSortingChange: handleTableSortingChange,
        onColumnSizingChange: setColumnSizing,
        onPaginationChange: setPagination,
        enableSorting,
        manualSorting,
        enablePinning: true,
        onColumnPinningChange: handleColumnPinningChange,
        onColumnOrderChange: setColumnOrder,
        initialState: {
            sorting: initialSortingState,
            pagination: { pageIndex: 0, pageSize: 10 },
        },
        state: {
            columnFilters,
            columnSizing,
            rowSelection,
            sorting: sortingState,
            columnOrder,
            columnVisibility,
            columnPinning: effectiveColumnPinning,
            pagination: effectivePagination,
        },
        getRowId: getRowId ?? ((row) => row.id)
    });

    useEffect(() => {
        if (onSelectedRowsChange) {
            onSelectedRowsChange(
                table.getSelectedRowModel().flatRows.map(row => row.original)
            );
        }
    }, [rowSelection, table, onSelectedRowsChange]);

    useEffect(() => {
        if (storedStateKey) {
            setStoredState((prev) => ({
                ...prev,
                sorting: sortingState,
            }));
            if (onSortingChange) {
                onSortingChange(sortingState);
            }
            // Persist sorting changes to the server (per user, per organization) so
            // that browse-list sort selections survive across sessions / devices
            // Only do this for user-initiated changes; hydration from
            // server preferences also flows through setSortingState but must not
            // PUT back to the server.
            if (isUserSortChangeRef.current) {
                isUserSortChangeRef.current = false;
                updateColumns(false, sortingState);
            }
        }
    }, [sortingState]);

    // Mirror the sorting-persist path for column pinning: mirror the current
    // pin state into localStorage on every change, and PUT to the server only
    // when the change originated from the user (burger menu toggle).
    // Hydration from the server also updates `columnPinning` and must not
    // trigger a redundant round-trip.
    useEffect(() => {
        if (!storedStateKey) return;
        setStoredState((prev) => ({
            ...prev,
            columnPinning,
        }));
        if (isUserPinChangeRef.current) {
            isUserPinChangeRef.current = false;
            updateColumns();
        }
    }, [columnPinning]);

    const extractAccessorWidths = (columns: any) => columns.reduce((acc: any, item: any) => {
        const key = item.id && item.id !== ACTIONS_COL ? item.id : item.accessorKey;
        if (key && item.size !== undefined) acc[key] = item.size;
        return acc;
    }, {});

    const [defaultColumnWidths, setDefaultColumnWidths] = useState({});

    useEffect(() => {
        columnSizingRef.current = columnSizing;
    }, [columnSizing]);

    useEffect(() => {
        if (columns.length > 0) {
            const widths = extractAccessorWidths(columns);
            setDefaultColumnWidths(widths);
        }
    }, [columns]);

    useEffect(() => {
        columnVisibilityRef.current = columnVisibility;
    }, [columnVisibility]);

    useEffect(() => {
        reArrangedColumnsRef.current = reArrangedColumns;
    }, [reArrangedColumns]);

    const requestNextPage = () => {
        if (fetchingRef.current || !onInfiniteScrollRef.current) {
            return;
        }

        fetchingRef.current = true;
        const requestId = ++nextPageRequestIdRef.current;
        scrollHeightAtFetchRef.current = infiniteScrollRef.current?.scrollHeight ?? 0;
        onInfiniteScrollRef.current();
        // The callback no-ops when there is no next page, and that does not
        // flip isLoadingNextPage. Leave the latch set and the grid never asks
        // again, even after the user scrolls back (LAX-10547).
        requestAnimationFrame(() => {
            if (requestId !== nextPageRequestIdRef.current) {
                return;
            }
            if (!isLoadingNextPageRef.current) {
                fetchingRef.current = false;
            }
        });
    };

    useEffect(() => {
        const container = infiniteScrollRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (isPastInfiniteScrollThreshold(scrollTop, scrollHeight, clientHeight)) {
                requestNextPage();
            }
        };

        // At the bottom, further wheel movement does not emit scroll events.
        // ui-grid had the same hole (LAX-10230); without this the next page
        // only loads after the user scrolls back up.
        const handleWheel = (event: WheelEvent) => {
            if (event.deltaY <= 0) {
                return;
            }

            const { scrollTop, scrollHeight, clientHeight } = container;
            if (
                isUnfilledScrollContainer(scrollHeight, clientHeight) ||
                isNearScrollBottom(scrollTop, scrollHeight, clientHeight)
            ) {
                requestNextPage();
            }
        };

        container.addEventListener('scroll', handleScroll);
        container.addEventListener('wheel', handleWheel, { passive: true });
        return () => {
            container.removeEventListener('scroll', handleScroll);
            container.removeEventListener('wheel', handleWheel);
        };
    }, []);

    useEffect(() => {
        if (isLoadingNextPage) {
            wasLoadingNextPageRef.current = true;
            return;
        }

        fetchingRef.current = false;
        if (!wasLoadingNextPageRef.current) {
            return;
        }

        wasLoadingNextPageRef.current = false;
        const container = infiniteScrollRef.current;
        if (!container) {
            return;
        }

        // An empty page does not grow the list. Chaining off that would
        // request forever while the viewport stays at the bottom.
        if (container.scrollHeight <= scrollHeightAtFetchRef.current) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = container;
        if (
            isUnfilledScrollContainer(scrollHeight, clientHeight) ||
            isNearScrollBottom(scrollTop, scrollHeight, clientHeight)
        ) {
            requestNextPage();
        }
    }, [isLoadingNextPage]);

    // Clear selection when clicking outside the grid
    useEffect(() => {
        if (!enableRowSelection) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (gridContainerRef.current && !gridContainerRef.current.contains(event.target as Node)) {
                // Check if there are any selected rows
                if (Object.keys(rowSelection).length > 0) {
                    setRowSelection({});
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [rowSelection, enableRowSelection]);

    useEffect(() => {
        const ids = columns.map((col: any) => String(col.id ?? col.accessorKey ?? ''));
        // LAX-10039: key the signature on the *set* of column ids (sorted),
        // not the sequence, so simply re-rendering with the same columns in a
        // different order (e.g. the user's saved order vs the raw prop order
        // on mount) doesn't count as a structural change and doesn't wipe out
        // the persisted order.
        const signature = [...ids].sort().join('\0');
        if (signature === columnStructureSignatureRef.current) {
            return;
        }
        columnStructureSignatureRef.current = signature;
        // Preserve the user's saved order (seeded from `storedState.activeColumns`
        // in `useGridPreferences` / the `reArrangedColumns` initializer):
        // keep existing ids in their current sequence, append any newly added
        // ids at the end, and drop ids that no longer exist. This matches the
        // legacy behaviour where reloading or switching category kept your
        // drag-reordered layout intact instead of snapping back to defaults.
        setColumnOrder((prev) => {
            const idSet = new Set(ids);
            const kept = (prev ?? []).filter((id: string) => idSet.has(id));
            const keptSet = new Set(kept);
            const missing = ids.filter((id) => !keptSet.has(id));
            return [...kept, ...missing];
        });
        setReArrangedColumns((prev: any) => {
            const columnMap = new Map(columns.map((c: any) => [c.id || c.accessorKey, c]));
            const prevList = Array.isArray(prev) ? prev : [];
            const ordered = prevList
                .map((c: any) => columnMap.get(c.id || c.accessorKey || c.name))
                .filter(Boolean);
            const keptIds = new Set(ordered.map((c: any) => c.id || c.accessorKey));
            const missing = columns.filter((c: any) => !keptIds.has(c.id || c.accessorKey));
            return [...ordered, ...missing];
        });
    }, [columns, setColumnOrder]);

    const updateColumns = (reset = false, sortingOverride?: SortingState, orderedColumnsOverride?: any[]) => {
        const timeout = setTimeout(() => {
            const activeColumns = reset
                ? columns
                : (orderedColumnsOverride ?? reArrangedColumnsRef.current ?? columns);
            const columnWidth = { ...extractAccessorWidths(columns), ...columnSizingRef.current };
            const filtered = activeColumns.filter((col: any) => (col.id || col.accessorKey) !== ACTIONS_COL);
            updateGridPreferences(filtered.map((col: any) => {
                const colId = col.id || col.accessorKey;
                return {
                    id: col.id,
                    accessorKey: col.accessorKey,
                    width: colId === 'select' ? 40 : Math.max(50, columnWidth[colId] ?? 100),
                    visible: columnVisibilityRef.current[colId] ?? true,
                };
            }), sortingOverride);
        }, 300);

        return () => clearTimeout(timeout);
    };

    useEffect(() => {
        if (storedStateKey) {
            setStoredState((prev) => ({
                ...prev,
                columnSizing: { ...extractAccessorWidths(columns), ...columnSizing },
                columnVisibility,
            }));
        }
    }, [columnSizing, columnVisibility, reArrangedColumns]);

    const currentColumnWidths = columnSizing;

    const currentColumnOrder = columnOrder;
    const defaultColumnOrder = columns.map((col: any) => col.id || col.accessorKey);

    useImperativeHandle(ref, () => ({
        focus: () => { if (infiniteScrollRef.current) infiniteScrollRef.current.focus(); },
        scrollToTop: () => { if (infiniteScrollRef.current) infiniteScrollRef.current.scrollTop = 0; },
        resetColumnsSort: (e: any) => resetColumnsSort(e),
        goToLastPage: () => {
            if (shouldShowPaginationFlag && table) {
                const pageCount = table.getPageCount();
                if (pageCount > 0) {
                    table.setPageIndex(pageCount - 1);
                }
            }
        },
        setRowSelection: (state: RowSelectionState) => setRowSelection(state),
    }));

    const handleDragStart = (e: React.DragEvent, columnId: string) => {
        if (columnId === 'select') return;
        if (isResizing || columnId === ACTIONS_COL) {
            e.preventDefault();
            return;
        }
        // LAX-10037: pinned columns keep their sticky position and must not
        // be dragged into or out of the center pinning group. The user
        // unpins first via the burger menu, then reorders.
        const col = table.getColumn(columnId);
        if (col && col.getIsPinned() !== false) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData('text/plain', columnId);
    };

    const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
        e.preventDefault();
        if (isResizing) return;

        const fromId = e.dataTransfer.getData('text/plain');
        if (fromId === ACTIONS_COL || targetColumnId === ACTIONS_COL || targetColumnId === 'select' || !fromId) return;
        const targetCol = table.getColumn(targetColumnId);
        if (targetCol && targetCol.getIsPinned() !== false) return;

        const newOrder = [...columnOrder];
        const fromIndex = newOrder.indexOf(fromId);
        const toIndex = newOrder.indexOf(targetColumnId);

        newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, fromId);

        const columnMap = new Map(columns.map((col: any) => [col.id || col.accessorKey, col]));
        const reorderedColumns: any = newOrder.map(key => columnMap.get(key)).filter(Boolean);

        setColumnOrder(newOrder);
        setReArrangedColumns(reorderedColumns);
        // Keep the ref in sync immediately so updateColumns persists the new order.
        // Without this we race a useEffect that updates the ref later, causing the
        // previous order to be saved.
        reArrangedColumnsRef.current = reorderedColumns;

        setStoredState((prev) => ({ ...prev, activeColumns: reorderedColumns }));
        updateColumns(false, undefined, reorderedColumns);
    };

    const handleResizeStart = (e: React.MouseEvent | React.TouchEvent, resizeHandler: any) => {
        setIsResizing(true);
        document.body.style.pointerEvents = 'none';
        document.body.style.userSelect = 'none';
        resizeHandler(e);
        document.addEventListener('mouseup', handleResizeEnd);
        document.addEventListener('touchend', handleResizeEnd);
    };

    const handleResizeEnd = () => {
        setTimeout(() => {
            setIsResizing(false);
            document.body.style.pointerEvents = 'auto';
            document.body.style.userSelect = '';
            document.removeEventListener('mouseup', handleResizeEnd);
            document.removeEventListener('touchend', handleResizeEnd);
        }, 150);
        updateColumns();
    };

    const lastResizeHandleClickRef = useRef<{ columnId: string; time: number } | null>(null);
    const pendingResizeRef = useRef<{ startX: number; resizeHandler: (event: any) => void; mouseDownEvent: React.MouseEvent } | null>(null);

    const autoFitColumn = (columnId: string, origin: EventTarget | null) => {
        if (!autoFitColumnOnResizeHandleDoubleClick) return;
        if (columnId === ACTIONS_COL || columnId === 'select') return;
        const originEl = origin instanceof HTMLElement ? origin : null;
        const tableEl = originEl?.closest('table');
        if (!tableEl) return;
        const width = measureColumnAutoFitWidth(tableEl, columnId);
        const nextSizing = { ...columnSizingRef.current, [columnId]: width };
        columnSizingRef.current = nextSizing;
        setColumnSizing(nextSizing);
        updateColumns();
    };

    const beginResizeOnDrag = (e: React.MouseEvent, resizeHandler: (event: any) => void) => {
        pendingResizeRef.current = {
            startX: e.clientX,
            resizeHandler,
            mouseDownEvent: e,
        };
        const onMove = (moveEvent: MouseEvent) => {
            const pending = pendingResizeRef.current;
            if (!pending) return;
            if (Math.abs(moveEvent.clientX - pending.startX) < 4) return;
            const { resizeHandler: startResize, mouseDownEvent } = pending;
            pendingResizeRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            handleResizeStart(mouseDownEvent, startResize);
        };
        const onUp = () => {
            pendingResizeRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const handleResizeHandleMouseDown = (
        e: React.MouseEvent,
        columnId: string,
        resizeHandler: (event: any) => void,
    ) => {
        if (!autoFitColumnOnResizeHandleDoubleClick) {
            handleResizeStart(e, resizeHandler);
            return;
        }
        // Do not start a drag here. The first click of a double-click used
        // to call handleResizeStart, which sets pointer-events:none and
        // swallows the second click — so auto-fit never ran. Match ui-grid:
        // second click on the right border auto-fits; movement starts resize.
        e.preventDefault();
        e.stopPropagation();
        if (columnId === ACTIONS_COL || columnId === 'select') return;
        const now = Date.now();
        const last = lastResizeHandleClickRef.current;
        if (last && last.columnId === columnId && now - last.time < 400) {
            lastResizeHandleClickRef.current = null;
            pendingResizeRef.current = null;
            autoFitColumn(columnId, e.currentTarget);
            return;
        }
        lastResizeHandleClickRef.current = { columnId, time: now };
        beginResizeOnDrag(e, resizeHandler);
    };

    const toggleColumnVisibility = (key: string) => {
        setColumnVisibility(prevVisibility => {
            if (!(key in prevVisibility)) return prevVisibility;
            return { ...prevVisibility, [key]: !prevVisibility[key] };
        });
        updateColumns();
    };

    // LAX-10037: single entry point for the burger menu pin toggles. Wraps
    // TanStack's `column.pin` so the change flows through
    // `handleColumnPinningChange` -> `columnPinning` state -> useEffect
    // that persists to the server.
    const setColumnPin = (columnId: string, position: 'left' | 'right' | null) => {
        const col = table.getColumn(columnId);
        if (!col) return;
        isUserPinChangeRef.current = true;
        col.pin(position === null ? false : position);
    };

    const resetColumnsWidth = (e:any) => {
        e.preventDefault();

        const defaultColumnVisibility = Object.fromEntries(
            columns.map((column: any) => [column.id || column.accessorKey, true])
        );

        setColumnVisibility(defaultColumnVisibility);
        setColumnSizing({});
        setDefaultColumnWidths({});
        updateColumns(true);
    };

    const resetColumnsSort = (e:any) => {
        e.preventDefault();

        const defaultColumnOrder = columns.map((col: any) => col.id || col.accessorKey);

        setColumnOrder(defaultColumnOrder);
        setReArrangedColumns(columns);
        setStoredState({});
        updateColumns(true);
    };

    // LAX-10039: full reset entry for the burger menu, mirroring legacy
    // `application-controller.js#resetGridState` — clears the local blob,
    // removes the current sub-key from the parent server preference, and
    // reverts every in-memory column state (size, visibility, order, sort,
    // pinning) back to what the column defs specify.
    const resetGridState = (e: any) => {
        e.preventDefault();

        const defaultColumnVisibility = Object.fromEntries(
            columns.map((column: any) => {
                const id = column.id || column.accessorKey;
                return [id, column?.meta?.visible !== false];
            }),
        );
        const defaultColumnOrder = columns.map((col: any) => col.id || col.accessorKey);

        setColumnVisibility(defaultColumnVisibility);
        setColumnSizing({});
        setDefaultColumnWidths({});
        setColumnOrder(defaultColumnOrder);
        setReArrangedColumns(columns);
        setSortingState([]);
        setColumnPinning({});

        setStoredState({});
        if (storedStateKey) {
            deleteGridPreferences?.();
        }
    };

    const handleRowSelection = (row: any) => {
        const isSelected = rowSelection[row.id];
        setRowSelection(isSelected ? {} : { [row.id]: true });
        if (onSelectedRow) {
            onSelectedRow(isSelected ? null : row.original);
        }
    };

    // LAX-10037: cell keyboard navigation. Mirrors the legacy
    // `data-ui-grid-cellNav` behaviour: arrow keys move focus between
    // visible cells, Home/End jump to the first/last cell in the current
    // row. Skipped while the user is inside an editable form control so
    // arrow keys inside inputs / selects continue to work as expected.
    const isEditableTarget = (el: EventTarget | null): boolean => {
        if (!el || !(el instanceof HTMLElement)) return false;
        const tag = el.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
        if (el.isContentEditable) return true;
        // React-Select renders inside a container the input lives in; also
        // exclude anything explicitly marked as an editing cell so the
        // EditableItemCell keeps ownership of the keyboard while in edit
        // mode. `.select-trigger` covers clicks/focus on the custom
        // SingleValue (sibling of the hidden combobox input).
        return !!el.closest('[data-editing-cell="true"], .select-trigger, .headless-combobox-trigger');
    };

    const focusCell = (cell: HTMLElement | null) => {
        if (!cell) return;
        cell.focus();
        // Keep the focused cell in view for tall / wide tables. `preventScroll`
        // on focus() is not enough — we need scrollIntoView with block:'nearest'
        // so pinned columns don't push the focused cell out of the viewport.
        if (typeof cell.scrollIntoView === 'function') {
            cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    };

    const handleGridKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const key = e.key;
        if (
            key !== 'ArrowLeft' && key !== 'ArrowRight'
            && key !== 'ArrowUp' && key !== 'ArrowDown'
            && key !== 'Home' && key !== 'End'
        ) {
            return;
        }
        if (isEditableTarget(e.target)) return;

        const cell = (e.target as HTMLElement).closest('td[data-column-id]') as HTMLTableCellElement | null;
        if (!cell) return;
        const row = cell.parentElement as HTMLTableRowElement | null;
        if (!row) return;
        const rowCells = Array.from(row.querySelectorAll<HTMLTableCellElement>('td[data-column-id]'));
        const currentIndex = rowCells.indexOf(cell);
        if (currentIndex === -1) return;

        e.preventDefault();

        if (key === 'ArrowLeft') {
            focusCell(rowCells[currentIndex - 1] ?? null);
            return;
        }
        if (key === 'ArrowRight') {
            focusCell(rowCells[currentIndex + 1] ?? null);
            return;
        }
        if (key === 'Home') {
            focusCell(rowCells[0] ?? null);
            return;
        }
        if (key === 'End') {
            focusCell(rowCells[rowCells.length - 1] ?? null);
            return;
        }

        // ArrowUp / ArrowDown: locate the cell with the same data-column-id
        // in the previous / next row. Skip non-data rows (loader / empty
        // state rows have no data-column-id cells so `siblingCell` is null).
        const columnId = cell.getAttribute('data-column-id');
        if (!columnId) return;
        const rows = Array.from(row.parentElement?.querySelectorAll<HTMLTableRowElement>('tr') ?? []);
        const rowIndex = rows.indexOf(row);
        if (rowIndex === -1) return;
        const step = key === 'ArrowUp' ? -1 : 1;
        for (let i = rowIndex + step; i >= 0 && i < rows.length; i += step) {
            const sibling = rows[i].querySelector<HTMLTableCellElement>(`td[data-column-id="${CSS.escape(columnId)}"]`);
            if (sibling) {
                focusCell(sibling);
                return;
            }
        }
    };

    // Move focus to the clicked cell so the arrow-key navigation above has
    // an initial anchor. Skips clicks on interactive elements (buttons,
    // links, inputs, comboboxes) that manage their own focus.
    const handleGridClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isInteractiveCellTarget(e.target)) {
            return;
        }
        const cell = (e.target as HTMLElement | null)?.closest('td[data-column-id]') as HTMLTableCellElement | null;
        if (cell) focusCell(cell);
    };

    return (
        <div
            ref={gridContainerRef}
            className={cn(
                'tw-flex tw-flex-col',
                shouldShowPaginationFlag ? 'tw-h-full tw-gap-3' : '',
                className
            )}
        >
            {toolbar && toolbar(table)}
            <div
                className={cn(
                    'tw-flex-1 tw-border tw-relative border-table',
                    shouldShowPaginationFlag
                        ? 'tw-overflow-x-auto tw-overflow-y-hidden'
                        : disableHorizontalScroll
                            ? 'tw-overflow-y-auto tw-overflow-x-hidden'
                            : 'tw-overflow-auto',
                    'tw-scrollbar-force', maxHeight
                )}
                ref={infiniteScrollRef}
                onKeyDown={handleGridKeyDown}
                onClick={handleGridClick}
            >
                <Table className={cn('tw-table-fixed', maxHeight, { 'tw-h-full': !shouldShowPaginationFlag })} containerClassName={cn({ 'tw-h-full': !shouldShowPaginationFlag })} id={id}>
                    <TableHeader>
                        {table.getHeaderGroups().map(headerGroup => {
                            const allHidden = headerGroup.headers.every(
                                header => !columnVisibility[header.column.id]
                            );

                            if (allHidden && burgerMenu) {
                                return (
                                    <TableRow key="empty-burger-header" className='tw-bb-0 !tw-border-b-0'>
                                        <TableHead className="tw-relative" colSpan={1}>
                                            <div
                                                id="burger-button"
                                                onClick={() => setIsBurgerMenuOpen(prev => !prev)}
                                                className="tw-cursor-pointer tw-text-xl tw-rounded-md tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-pr-3"
                                            >
                                                ☰
                                            </div>
                                            <GridBurgerMenu
                                                columns={columns}
                                                storedStateKey={storedStateKey}
                                                columnVisibility={columnVisibility}
                                                isOpen={isBurgerMenuOpen}
                                                onClose={() => setIsBurgerMenuOpen(false)}
                                                toggleColumnVisibility={toggleColumnVisibility}
                                                resetColumnsWidth={resetColumnsWidth}
                                                resetColumnsSort={resetColumnsSort}
                                                resetGridState={resetGridState}
                                                setWrapContent={setWrapContent}
                                                wrapContent={wrapContent}
                                                currentColumnWidths={currentColumnWidths}
                                                defaultColumnWidths={defaultColumnWidths}
                                                currentColumnOrder={currentColumnOrder}
                                                defaultColumnOrder={defaultColumnOrder}
                                                columnPinning={effectiveColumnPinning}
                                                setColumnPin={setColumnPin}
                                            />
                                        </TableHead>
                                    </TableRow>
                                );
                            }

                            return (
                                <TableRow key={headerGroup.id} className="table-header-row !tw-border-b-0">
                                    {headerGroup.headers.map((header, index) => {
                                        const isLastColumn = index === headerGroup.headers.length - 1;
                                        const pinnedSide = header.column.getIsPinned();
                                        const isLeftPinned = pinnedSide === 'left' || !!header.column.columnDef.meta?.leftPinned;
                                        const isRightPinned = pinnedSide === 'right' || !!header.column.columnDef.meta?.pinned;
                                        let leftOffset: number | undefined;
                                        let rightOffset: number | undefined;
                                        if (pinnedSide === 'left') {
                                            leftOffset = header.column.getStart('left');
                                        } else if (pinnedSide === 'right') {
                                            const rightCols = table.getRightVisibleLeafColumns();
                                            const idx = rightCols.findIndex((c) => c.id === header.column.id);
                                            let off = 0;
                                            for (let i = idx + 1; i < rightCols.length; i++) {
                                                off += rightCols[i].getSize();
                                            }
                                            rightOffset = off;
                                        }
                                        const headerLabel = typeof header.column.columnDef.header === 'string'
                                            ? header.column.columnDef.header
                                            : undefined;
                                        return (
                                            <TableHead
                                                key={header.id}
                                                className={cn(`tw-relative ${thClassName}`, {
                                                    'resizing': header.column.getIsResizing()
                                                })}
                                                style={header.column.id === 'select' ? undefined : {
                                                    width: Math.max(40, inRange(header.getSize(), 20, 33)
                                                        ? 33
                                                        : header.getSize()),
                                                    textOverflow: 'ellipsis',
                                                }}
                                                pinned={isRightPinned}
                                                leftPinned={isLeftPinned}
                                                leftOffset={leftOffset}
                                                rightOffset={rightOffset}
                                                draggable={!isResizing && header.column.id !== ACTIONS_COL && header.column.getIsPinned() === false}
                                                onDragStart={e => handleDragStart(e, header.column.id)}
                                                onDragOver={e => e.preventDefault()}
                                                onDrop={e => handleDrop(e, header.column.id)}
                                                data-column-id={header.column.id}
                                                title={headerLabel}
                                            >
                                                {header.isPlaceholder ? null : (
                                                    <div
                                                        className={cn(
                                                            'tw-flex tw-w-full tw-min-w-0 tw-items-center group',
                                                            truncateHeaders ? undefined : 'tw-truncate',
                                                            {
                                                                'tw-cursor-pointer':
                                                                header.column.getCanSort() && header.column.id !== ACTIONS_COL,
                                                            },
                                                        )}
                                                        onClick={header.column.getCanSort() && header.column.id !== ACTIONS_COL
                                                            ? header.column.getToggleSortingHandler()
                                                            : undefined}
                                                    >
                                                        {truncateHeaders
                                                            && header.column.id !== ACTIONS_COL
                                                            && header.column.id !== 'select' ? (
                                                                <span className="tw-min-w-0 tw-flex-1 tw-truncate">
                                                                    {flexRender(
                                                                        header.column.columnDef.header,
                                                                        header.getContext()
                                                                    )}
                                                                </span>
                                                            ) : (
                                                                flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )
                                                            )}
                                                        {header.column.id !== ACTIONS_COL && (
                                                            <GridSortingCaret
                                                                direction={header.column.getIsSorted()}
                                                                index={header.column.getSortIndex()}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                                {header.column.getCanResize() && (
                                                    <div
                                                        data-column-resize-handle={header.column.id}
                                                        className="tw-absolute tw-top-0 tw-bottom-0 tw-right-[-8.5px] tw-w-4 tw-cursor-col-resize tw-z-10"
                                                        onMouseDown={e => handleResizeHandleMouseDown(
                                                            e,
                                                            header.column.id,
                                                            header.getResizeHandler(),
                                                        )}
                                                        onTouchStart={e => handleResizeStart(e, header.getResizeHandler())}
                                                        onDoubleClick={e => {
                                                            if (!autoFitColumnOnResizeHandleDoubleClick) return;
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            lastResizeHandleClickRef.current = null;
                                                            autoFitColumn(header.column.id, e.currentTarget);
                                                        }}
                                                    >
                                                        <div
                                                            className={cn(
                                                                'tw-absolute tw-top-0 tw-bottom-0 tw-left-1/2 tw-w-px tw-transform -tw-translate-x-1/2',
                                                                header.column.getIsResizing()
                                                                    ? 'tw-bg-gray-900'
                                                                    : 'tw-bg-gray-300'
                                                            )}
                                                        />
                                                    </div>
                                                )}
                                                {isLastColumn && burgerMenu && (
                                                    <>
                                                        <div
                                                            id="burger-button"
                                                            onClick={() =>
                                                                setIsBurgerMenuOpen(prev => !prev)
                                                            }
                                                            className="!tw-px-0"
                                                        >
                                                            <div className="tw-cursor-pointer tw-text-xl tw-rounded-md tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-pr-3">
                                                                ☰
                                                            </div>
                                                        </div>
                                                        <GridBurgerMenu
                                                            columns={columns}
                                                            storedStateKey={storedStateKey}
                                                            columnVisibility={columnVisibility}
                                                            isOpen={isBurgerMenuOpen}
                                                            onClose={() => setIsBurgerMenuOpen(false)}
                                                            toggleColumnVisibility={
                                                                toggleColumnVisibility
                                                            }
                                                            resetColumnsWidth={resetColumnsWidth}
                                                            resetColumnsSort={resetColumnsSort}
                                                            resetGridState={resetGridState}
                                                            setWrapContent={setWrapContent}
                                                            wrapContent={wrapContent}
                                                            currentColumnWidths={currentColumnWidths}
                                                            defaultColumnWidths={defaultColumnWidths}
                                                            currentColumnOrder={currentColumnOrder}
                                                            defaultColumnOrder={defaultColumnOrder}
                                                            columnPinning={effectiveColumnPinning}
                                                            setColumnPin={setColumnPin}
                                                        />
                                                    </>
                                                )}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableHeader>

                    <TableBody>
                        {isLoading ? (
                            <TableRow className="tw-pointer-events-none" style={{ height: '100%' }}>
                                <TableCell colSpan={columns.length} className="tw-h-24 tw-text-center">
                                    <span className="tw-flex tw-items-center tw-justify-center">
                                        <Loader />
                                    </span>
                                </TableCell>
                            </TableRow>
                        ) : table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map(row => (
                                <GridRow
                                    selected={rowSelection[row.id]}
                                    key={row.id}
                                    wrapContent={isWrapContent ? false : wrapContent}
                                    row={row}
                                    onClick={enableRowSelection ? (e: React.MouseEvent) => {
                                        if (isInteractiveCellTarget(e.target)) return;
                                        handleRowSelection(row);
                                    } : undefined}
                                    enableRowSelection={enableRowSelection}
                                    hasPadding={!insideItemEditor}
                                    rowWrapper={rowWrapper}
                                />
                            ))
                        ) : (
                            <TableRow style={{ height: '100%' }}>
                                <TableCell colSpan={columns.length} className="tw-h-24 tw-text-center">
                                    <span>{t('NODATA.NO_DATA_FOUND')}</span>
                                </TableCell>
                            </TableRow>
                        )}
                        {isLoadingNextPage && (
                            <TableRow style={{ height: '100%' }}>
                                <TableCell colSpan={columns.length} className="tw-h-24 tw-text-center">
                                    <span className="tw-flex tw-items-center tw-justify-center">
                                        <Loader />
                                    </span>
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow style={{ height: '100%' }}></TableRow>
                    </TableBody>
                </Table>
            </div>
            {shouldShowPaginationFlag && (
                <PaginationPage
                    totalItems={table.getFilteredRowModel().rows.length}
                    currentPage={table.getState().pagination.pageIndex + 1}
                    onPageChange={(page: number) => table.setPageIndex(page - 1)}
                    navigation={paginationNavigation}
                />
            )}
        </div>
    );

});

Grid.displayName = 'Grid';

export { Grid };
